/**
 * Pre-migration safety copies, kept in their own IndexedDB database so they
 * survive even if the main store is what a migration corrupts. Browser only;
 * on native, Capacitor's own database file is the backup surface.
 *
 * A snapshot is a complete SQLite file plus the version range it was taken at,
 * so a failed catch-up upgrade can be rolled back wholesale and, if need be,
 * downloaded.
 */
const DB_NAME = 'okane-backups'
const STORE = 'snapshots'
const KEEP = 3

const available = () => typeof indexedDB !== 'undefined'

function open() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => {
      const store = req.result.createObjectStore(STORE, { keyPath: 'id' })
      store.createIndex('at', 'at')
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function tx(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode)
    const req = fn(t.objectStore(STORE))
    t.oncomplete = () => resolve(req?.result)
    t.onerror = () => reject(t.error)
  })
}

/** Signature matches runMigrations' `backup` hook. Never throws upward: a
 *  failed backup must not itself block a migration the user needs. */
export async function saveBackup(bytes, meta = {}) {
  if (!available()) return null
  try {
    const db = await open()
    const id = `${meta.at ?? new Date().toISOString()}-v${meta.from ?? '?'}`
    // Copy the bytes — the live buffer may be reused by the driver.
    await tx(db, 'readwrite', (s) => s.put({ id, at: meta.at ?? new Date().toISOString(), meta, bytes: bytes.slice() }))
    await prune(db)
    return id
  } catch (err) {
    console.error('OKANE could not store a pre-migration backup', err)
    return null
  }
}

async function prune(db) {
  const all = await tx(db, 'readonly', (s) => s.getAll())
  const extra = (all ?? []).sort((a, b) => String(b.at).localeCompare(a.at)).slice(KEEP)
  if (extra.length) await tx(db, 'readwrite', (s) => extra.forEach((r) => s.delete(r.id)))
}

export async function listBackups() {
  if (!available()) return []
  const db = await open()
  const all = (await tx(db, 'readonly', (s) => s.getAll())) ?? []
  return all
    .sort((a, b) => String(b.at).localeCompare(a.at))
    .map(({ id, at, meta, bytes }) => ({ id, at, meta, size: bytes?.byteLength ?? 0 }))
}

export async function getBackup(id) {
  if (!available()) return null
  const db = await open()
  return tx(db, 'readonly', (s) => s.get(id))
}

export const hasBackups = async () => (await listBackups()).length > 0
